import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Gift, Loader2, CheckCircle2, Copy, AlertTriangle, ArrowRight } from "lucide-react";

/*  PACK COMP REDEMPTION — /packs/comp/{token}
    The recipient of a comp-a-crew gift lands here. We validate the token,
    show the pack details, and let them one-tap claim. On success we show
    the access code they also received by email.
*/
const PackComp = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading]   = useState(true);
  const [data, setData]         = useState(null);
  const [error, setError]       = useState("");
  const [claiming, setClaiming] = useState(false);
  const [claim, setClaim]       = useState(null);

  useEffect(() => {
    api.get(`/comp-crew/${encodeURIComponent(token)}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "That comp link isn't valid."))
      .finally(() => setLoading(false));
  }, [token]);

  const doClaim = async () => {
    if (claiming) return;
    setClaiming(true);
    try {
      const { data: c } = await api.post(`/comp-crew/${encodeURIComponent(token)}/claim`);
      setClaim(c);
      toast.success("Pack claimed. Access code generated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't claim the pack.");
    } finally {
      setClaiming(false);
    }
  };

  const copy = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Copied.");
    } catch {}
  };

  if (loading) {
    return (
      <div className="grain min-h-screen bg-[#0a0908] flex items-center justify-center text-[#a39797]">
        <Loader2 size={16} className="animate-spin" /> &nbsp; Validating your comp…
      </div>
    );
  }

  if (error) {
    return (
      <div className="grain min-h-screen bg-[#0a0908] text-[#f4f0ec] flex items-center justify-center px-5">
        <div className="max-w-md text-center">
          <AlertTriangle size={40} className="text-[#8b0000] mx-auto mb-4" />
          <div className="font-display text-2xl mb-2" data-testid="comp-error">That comp isn't valid.</div>
          <div className="text-sm text-[#a39797] mb-5">{error}</div>
          <Link to="/packs" className="btn-primary text-sm">Browse packs →</Link>
        </div>
      </div>
    );
  }

  // Already claimed state (either previously or just now)
  const effectiveClaim = claim || (data?.redeemed && data.pack_order_id ? { already: true, order_id: data.pack_order_id, pack_id: data.pack_id } : null);

  return (
    <div className="grain min-h-screen bg-[#0a0908] text-[#f4f0ec]">
      <div className="border-b border-[#2a2525] px-5 py-4 flex items-center justify-between">
        <Link to="/" className="text-[10px] tracking-[0.3em] text-[#d4af37]">◉ WASNTME.PARTY</Link>
        <div className="text-[9px] tracking-[0.3em] text-[#5a5050]">COMP CREW PACK</div>
      </div>

      <div className="max-w-xl mx-auto px-5 py-14">
        {!effectiveClaim ? (
          <div data-testid="comp-redeem-view">
            <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-3">◉ YOUR PACK IS WAITING</div>
            <h1 className="font-display text-4xl sm:text-5xl mb-3 leading-[1.05]">
              You just got comped a <span className="text-[#d4af37]">{data.pack_label}</span>.
            </h1>
            <p className="text-sm text-[#a39797] mb-6 leading-relaxed">
              {data.note || "A streamer thinks your crew should run this one. On the house. No credit card. No signup."}
            </p>

            <div className="card-noir p-5 mb-6" data-testid="comp-pack-details">
              <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-2">WHAT'S INSIDE</div>
              <div className="font-display text-xl mb-1">{data.pack_label}</div>
              {data.pack_tagline && <div className="text-xs text-[#a39797] mb-3 leading-relaxed">{data.pack_tagline}</div>}
              <div className="text-[11px] text-[#d4d0cc] flex flex-wrap gap-x-4 gap-y-1">
                {data.max_players && <span><Gift size={10} className="inline mr-1 text-[#d4af37]" /> Up to {data.max_players} players</span>}
                {data.cases_included && <span><Gift size={10} className="inline mr-1 text-[#d4af37]" /> {data.cases_included} case{data.cases_included > 1 ? "s" : ""} included</span>}
              </div>
            </div>

            <button onClick={doClaim} disabled={claiming} className="btn-primary w-full py-3" data-testid="comp-claim-btn">
              {claiming ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              {claiming ? "Minting your access code…" : "CLAIM MY PACK"}
            </button>
            <div className="text-[10px] text-[#5a5050] mt-3 text-center">
              This comp was gifted by <span className="text-[#d4af37]">{data.created_by === "admin" ? "WasntMe" : data.created_by}</span>.
            </div>
          </div>
        ) : (
          <div data-testid="comp-success-view">
            <CheckCircle2 size={56} className="text-[#d4af37] mb-4" />
            <div className="font-display text-3xl mb-2">Your pack is live.</div>
            <p className="text-sm text-[#a39797] mb-6 leading-relaxed">
              {effectiveClaim.already
                ? "You've already claimed this comp. Your access code is below (and in your email)."
                : "A copy of your access code was just emailed to you. Save this screen too."}
            </p>
            {claim?.access_code && (
              <div className="card-noir p-5 mb-5">
                <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-2">YOUR ACCESS CODE</div>
                <div className="font-mono text-2xl text-[#f4f0ec] break-all" data-testid="comp-access-code">{claim.access_code}</div>
                <button onClick={() => copy(claim.access_code)} className="btn-ghost text-xs mt-3" data-testid="comp-access-code-copy">
                  <Copy size={11} /> Copy
                </button>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <button onClick={() => navigate("/dashboard/" + (claim?.access_code || ""))}
                className="btn-primary text-sm" data-testid="comp-cta-dashboard">
                Open my pack <ArrowRight size={12} />
              </button>
              <Link to="/" className="btn-ghost text-xs">Back to the arena</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PackComp;
